

import { Component, Vue, Watch, Prop, Inject } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

import UserHeaderFields from "./CaseDetails/Users/UserHeaderFields.vue"
import User, { UserWithFields, LabeledValueWithFieldInfo } from "@/types/user"
import UserImageProvider from "@/services/userImageProvider"

const caseStore = namespace("cases")

@Component({
	components: {
		"user-header-fields": UserHeaderFields
	}
})
export default class UserImage extends Vue {
	@Inject({ from: "userImageProvider", default: null }) userImageProvider!: UserImageProvider | null

	@caseStore.Getter("getCurrentCase") public currentCase: any;

	@Getter("getUserPictureUrl")
	public userPictureUrl!: (user: { id: string}) => string;

	@Action
	public loadUserPictureUrl!: (user: { id: string}) => Promise<string>;

	@Prop({ type: String, default: "md" })
	public size!: string;

	@Prop()
	public user: any;

	@Prop({ default: null })
	public promisedUser!: Promise<User> | null;

	@Prop({ type: Boolean, default: false })
	public showUserName!: boolean;

	@Prop({ type: Boolean, default: false })
	public showTeams!: boolean;

	@Prop({ type: Boolean, default: false })
	public showEmail!: string;

	@Prop({ type: Boolean, default: false })
	public showHeaderFields!: boolean;

	@Prop({ type: Boolean, default: false })
	public usernamePopup!: boolean;

	@Prop({ type: String, default: "id" })
	public userIdField!: string;

	@Prop({ default: () => null })
	public fetchUserPictureUrl!: null | ((id: string) => string)

	@Prop({ type: String, default: null })
	private imageTooltip!: string | null

	private fetchedUser: any | null = null

	isLoaded: boolean = false;
	pictureError: boolean = false;
	pictureUrl: string | null = null;
	expandUserTeams: boolean = false;

	isFetchingPromisedUser: boolean = false;

	get userHeaderFields (): { [key: string]: LabeledValueWithFieldInfo } | null {
		return this.user ? this.user.userHeaderFieldValues : null
	}

	private get displayingUser (): any | null {
		if (this.user) return this.user
		if (this.promisedUser && !this.isFetchingPromisedUser) return this.fetchedUser

		return null
	}

	@Watch("user")
	onUserChanged (newUser: any, oldUser: any) {
		if (!newUser || !oldUser || newUser.id !== oldUser.id) {
			this.pictureError = false
			this.pictureUrl = null
			this.isLoaded = false
		}
	}

	@Watch("promisedUser")
	onPromisedUserChanged (newUser: Promise<User>, oldUser: any) {
		this.isFetchingPromisedUser = true
		newUser.then((user: User) => this.onPromisedUserResolved(user))
	}

	@Watch("userHeaderFields")
	onUserHeaderFieldsChanged (headerFields: { [key: string]: LabeledValueWithFieldInfo } | null) {
		if (this.displayingUser) {
			this.displayingUser.userHeaderFieldValues = headerFields
		}
	}

	private onPromisedUserResolved (user: User) {
		this.fetchedUser = user
		this.pictureError = false
		this.pictureUrl = null
		this.isFetchingPromisedUser = false
	}

	public mounted () {
		if (this.user) {
			this.onUserChanged(this.user, null)
		} else if (this.promisedUser) {
			this.onPromisedUserChanged(this.promisedUser, null)
		}
	}

	public get userPicture (): string | null {
		if (this.pictureError) {
			return "images/blank_user.png"
		}

		if (this.pictureUrl) {
			this.isLoaded = true
			return this.pictureUrl
		}

		if (this.displayingUser) {
			if (this.displayingUser.isExternal) {
				return "images/blank_user.png"
			}
			if (this.displayingUser.pictureUrl) {
				return this.displayingUser.pictureUrl
			}

			if (!this.displayingUser[this.userIdField]) {
				return this.pictureUrl = "images/system_user.png"
			}
			const userId = this.displayingUser[this.userIdField]

			if (this.userImageProvider) {
				return this.pictureUrl = (this.userImageProvider.getImageUrl(userId) || "images/blank_user.png")
			}

			const cachedPictureUrl = this.userPictureUrl(userId)
			if (cachedPictureUrl) {
				return this.pictureUrl = cachedPictureUrl
			}

			if (this.fetchUserPictureUrl) {
				this.pictureUrl = this.fetchUserPictureUrl(userId)
			} else {
				this.loadUserPictureUrl(userId).then((pictureUrl: string) => {
					this.pictureUrl = pictureUrl
				})
			}

			if (!this.pictureUrl) {
				return "images/blank_user.png"
			}

			return this.pictureUrl
		} else {
			return this.pictureUrl = "images/system_user.png"
		}
	}

	public loadedImage () {
		this.isLoaded = true
	}

	public imageUrlError (args: any) {
		this.pictureError = true
	}

	public get userTeamListDisplay (): string[] {
		if (!this.displayingUser) return []

		if (this.displayingUser.teams && this.expandUserTeams && this.displayingUser.teams.length > 0) {
			return this.displayingUser.teams.map((userTeam: {id: string, title: string}) => userTeam.title)
		} else if (this.expandUserTeams && (!this.displayingUser.teams || !this.displayingUser.teams.length)) {
			return [ `${this.displayingUser.name} is not a member of any Roles.` ]
		} else {
			return []
		}
	}
}

